// Editor.js
import React, { useState, useRef, useEffect } from 'react';
import HelloWorld from './HelloWorld';
import Group from './Group';
import Gizmo from './Gizmo';

export const Editor = () => {
  // The editor only manages groups and their transforms
  const [groups, setGroups] = useState({
    'group1': {
      id: 'group1',
      transform: {
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        panX: -150,
        panY: 0,
        translateZ: 0,
        scale: 1
      }
    },
    'group2': {
      id: 'group2',
      transform: {
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        panX: 150,
        panY: 0,
        translateZ: 0,
        scale: 1
      }
    }
  });
  
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const groupRefs = useRef({});
  const editorRef = useRef(null);
  
  // Track if we're dragging vs just clicking
  const isDragging = useRef(false);
  const startDragPosition = useRef({ x: 0, y: 0 });

  // Selection overlay component
  const SelectionOverlay = ({ selectedId }) => {
    const [rect, setRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
    
    useEffect(() => {
      if (!selectedId || !groupRefs.current[selectedId]) return;
      
      const updateRect = () => {
        const element = groupRefs.current[selectedId];
        const bounds = element.getBoundingClientRect();
        const editorBounds = element.parentElement.getBoundingClientRect();
        
        setRect({
          left: bounds.left - editorBounds.left,
          top: bounds.top - editorBounds.top,
          width: bounds.width,
          height: bounds.height
        });
      };
      
      updateRect();
      window.addEventListener('resize', updateRect);
      
      // Need to update rect when transform changes
      const intervalId = setInterval(updateRect, 100);
      
      return () => {
        window.removeEventListener('resize', updateRect);
        clearInterval(intervalId);
      };
    }, [selectedId, groups[selectedId]?.transform]);
    
    if (!selectedId) return null;
    
    return (
      <div style={{
        position: 'absolute',
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        border: '1px dashed rgba(0, 100, 255, 0.7)',
        pointerEvents: 'none',
        zIndex: 50
      }} />
    );
  };

  // Handle updating transform of the selected group
  const handleTransformChange = (changes) => {
    if (!selectedGroupId) return;
    
    setGroups(prev => {
      const updatedGroup = {...prev[selectedGroupId]};
      const updatedTransform = {...updatedGroup.transform};
      
      // Update individual transform properties
      Object.keys(changes).forEach(key => {
        if (key in updatedTransform) {
          updatedTransform[key] = changes[key];
        }
      });
      
      updatedGroup.transform = updatedTransform;
      
      return {
        ...prev,
        [selectedGroupId]: updatedGroup
      };
    });
  };
  
  // Get transform of currently selected group
  const getSelectedTransform = () => {
    if (!selectedGroupId || !groups[selectedGroupId]) {
      return {
        rotationX: 0, rotationY: 0, rotationZ: 0,
        panX: 0, panY: 0, translateZ: 0, scale: 1
      };
    }
    
    return groups[selectedGroupId].transform;
  };
  
  const selectedTransform = getSelectedTransform();

  // Set up editor-wide event handlers
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const handleMouseDown = (e) => {
      // Only handle if we have a selection and we're not clicking on a group
      if (selectedGroupId && e.target === editor) {
        if (e.button === 0) { // Left mouse button for pan
          isDragging.current = false; // Start as not dragging, will be set to true on move
          startDragPosition.current = { x: e.clientX, y: e.clientY };
        } else if (e.button === 1) { // Middle mouse button for rotate
          e.preventDefault(); // Prevent default middle-click behavior
          isDragging.current = false;
          startDragPosition.current = { x: e.clientX, y: e.clientY };
        }
      }
    };
    
    const handleMouseMove = (e) => {
      // Only handle moves if we have a selection
      if (selectedGroupId && startDragPosition.current) {
        const deltaX = e.clientX - startDragPosition.current.x;
        const deltaY = e.clientY - startDragPosition.current.y;
        
        // Check if we've moved enough to consider it a drag
        const distanceMoved = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (distanceMoved > 3) {
          isDragging.current = true;
        }
        
        // Handle left button (pan)
        if (e.buttons & 1) {
          handleTransformChange({
            panX: selectedTransform.panX + deltaX,
            panY: selectedTransform.panY + deltaY
          });
          startDragPosition.current = { x: e.clientX, y: e.clientY };
        }
        // Handle middle button (rotate)
        else if (e.buttons & 4) {
          const rotationDelta = (deltaX + deltaY) * 0.5;
          handleTransformChange({
            rotationZ: selectedTransform.rotationZ + rotationDelta
          });
          startDragPosition.current = { x: e.clientX, y: e.clientY };
        }
      }
    };
    
    const handleMouseUp = (e) => {
      // If it was just a click (not a drag) on the editor background, deselect
      if (e.target === editor && !isDragging.current && e.button === 0) {
        setSelectedGroupId(null);
      }
      
      isDragging.current = false;
      startDragPosition.current = null;
    };
    
    const handleWheel = (e) => {
      // Only handle wheel if we have a selection
      if (selectedGroupId) {
        e.preventDefault();
        const scaleFactor = e.deltaY < 0 ? 1.1 : 0.9;
        const newScale = Math.max(0.1, Math.min(10, selectedTransform.scale * scaleFactor));
        handleTransformChange({ scale: newScale });
      }
    };
    
    // Add event listeners
    editor.addEventListener('mousedown', handleMouseDown);
    editor.addEventListener('mousemove', handleMouseMove);
    editor.addEventListener('mouseup', handleMouseUp);
    editor.addEventListener('wheel', handleWheel, { passive: false });
    
    // Cleanup
    return () => {
      editor.removeEventListener('mousedown', handleMouseDown);
      editor.removeEventListener('mousemove', handleMouseMove);
      editor.removeEventListener('mouseup', handleMouseUp);
      editor.removeEventListener('wheel', handleWheel);
    };
  }, [selectedGroupId, selectedTransform, handleTransformChange]);

  return (
    <div 
      ref={editorRef}
      style={{ 
        width: '100%', 
        height: '500px',
        position: 'relative',
        backgroundColor: '#f5f5f5',
        overflow: 'hidden',
        outline: 'none',
        cursor: selectedGroupId ? 'move' : 'default'
      }}
      tabIndex={0}
    >
      {/* Group 1 with HelloWorld */}
      <Group 
        key="group1"
        ref={el => groupRefs.current['group1'] = el}
        {...groups['group1'].transform}
        onClick={() => setSelectedGroupId('group1')}
      >
        <HelloWorld />
      </Group>
      
      {/* Group 2 with HelloWorld */}
      <Group 
        key="group2"
        ref={el => groupRefs.current['group2'] = el}
        {...groups['group2'].transform}
        onClick={() => setSelectedGroupId('group2')}
      >
        <HelloWorld initialText="Hello World 2" />
      </Group>
      
      {/* Selection overlay at editor level */}
      <SelectionOverlay selectedId={selectedGroupId} />
      
      {/* Gizmo */}
      {selectedGroupId && groupRefs.current[selectedGroupId] && (
        <Gizmo 
          targetObject={groupRefs.current[selectedGroupId]}
          {...selectedTransform}
        />
      )}
      
      {/* Minimal status display */}
      {selectedGroupId && (
        <div style={{ 
          position: 'absolute', 
          bottom: '10px', 
          left: '10px', 
          background: 'rgba(0,0,0,0.5)', 
          color: 'white',
          padding: '5px',
          borderRadius: '3px',
          fontSize: '12px',
          fontFamily: 'monospace'
        }}>
          <div>Group: {selectedGroupId}</div>
          <div>X: {selectedTransform.panX.toFixed(0)} Y: {selectedTransform.panY.toFixed(0)} Z: {selectedTransform.rotationZ.toFixed(0)}° S: {selectedTransform.scale.toFixed(2)}x</div>
        </div>
      )}
    </div>
  );
};

export default Editor;